// Copyright 2024, Imprivata, Inc.  All rights reserved.

import { Button, InputBox } from '@imprivata-cloud/components';
import { Form } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Appearance.less';
import { UndoOutlined } from '@ant-design/icons';
import { Entitlement, SystemType } from '../../../api/constants';
import { orgPreferencesGet } from '../../../api/portalServices';
import type { OrgPreferencesGetResponse } from '../../../api/types';
import { hasEntitlement } from '../../../api/utils';
import { useSession } from '../../../common/authorization/context/Session';
import { withRole } from '../../../common/authorization/Roles';
import LogoUpload from '../../../components/logoUpload/LogoUpload';
import { useErrorContext } from '../../../errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '../../../errorHandler/errors';
import useOrganizationInfoSave from '../../../setup/hooks/useOrganizationInfoSave';
import { ROLE_TYPE_ADMIN } from '../../constants';

enum FieldNames {
  LOGO = 'logo',
  ORG_NAME = 'orgName',
  USERNAME_LABEL = 'usernameLabel',
  PASSWORD_LABEL = 'passwordLabel',
  PROX_CARD_LABEL = 'proxCardLabel',
}

interface FormValues {
  [FieldNames.ORG_NAME]: string;
  [FieldNames.LOGO]: File;
  [FieldNames.USERNAME_LABEL]?: string;
  [FieldNames.PASSWORD_LABEL]?: string;
  [FieldNames.PROX_CARD_LABEL]?: string;
}

const TRANSLATION_ROOT = 'portal.appearance';
const AUTH_METHOD_LABEL_MAX_LENGTH = 24;

function Appearance() {
  const { t } = useTranslation();
  const { tenantInfo } = useSession();
  const canCustomizeLabels = hasEntitlement(tenantInfo, Entitlement.SSO);
  const [form] = Form.useForm<FormValues>();
  const { emitError, clearErrorNotifications } = useErrorContext();
  const organizationInfoSave = useOrganizationInfoSave(SystemType.PORTAL);
  const orgPreferencesGetResponse = useRef<OrgPreferencesGetResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      clearErrorNotifications();
      setIsLoading(true);
      const orgPreferences = await orgPreferencesGet();
      form.setFieldValue(FieldNames.ORG_NAME, orgPreferences.orgName);
      form.setFieldValue(FieldNames.USERNAME_LABEL, orgPreferences.usernameLabel ?? '');
      form.setFieldValue(FieldNames.PASSWORD_LABEL, orgPreferences.passwordLabel ?? '');
      form.setFieldValue(FieldNames.PROX_CARD_LABEL, orgPreferences.proxCardLabel ?? '');
      form.setFieldValue([FieldNames.LOGO], undefined);
      orgPreferencesGetResponse.current = orgPreferences;
      setIsFormDirty(false);
      setIsLoading(false);
      // this will clear errors in the form if any left from pre-fetch user changes
      await form.validateFields();
    } catch (e) {
      emitError(new AppError(e as ApiError));
    }
  }, [form.setFieldValue, emitError, clearErrorNotifications, form.validateFields]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  async function onFinish(formValues: FormValues) {
    if (!orgPreferencesGetResponse.current?.orgName) {
      emitError(AppError.fromMessage('Invalid state exception: orgName cannot be empty.'));
      return;
    }
    if (!isFormDirty) {
      return;
    }
    try {
      setIsLoading(true);
      clearErrorNotifications();
      await organizationInfoSave.mutateAsync({
        orgName: formValues[FieldNames.ORG_NAME],
        usernameLabel: formValues[FieldNames.USERNAME_LABEL] || orgPreferencesGetResponse.current.usernameLabel,
        passwordLabel: formValues[FieldNames.PASSWORD_LABEL] || orgPreferencesGetResponse.current.passwordLabel,
        proxCardLabel: formValues[FieldNames.PROX_CARD_LABEL] || orgPreferencesGetResponse.current.proxCardLabel,
        logoFile: formValues[FieldNames.LOGO],
        oldLogoUrl: orgPreferencesGetResponse.current?.logoUrl,
      });
      await fetchData();
    } catch (e) {
      emitError(new AppError(e as ApiError));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className={'appearance-container'} aria-description="appearance-form">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onValuesChange={() => setIsFormDirty(true)}
        className="appearance-form"
      >
        <section className="appearance-card appearance-fields">
          <h2 className="section-title">{t(`${TRANSLATION_ROOT}.org-info-title`)}</h2>

          <Form.Item name={FieldNames.ORG_NAME} rules={[{ required: true }]}>
            <InputBox label={t(`${TRANSLATION_ROOT}.org-name-label`)} autoFocus />
          </Form.Item>

          <LogoUpload
            isLoading={isLoading}
            oldImageUrl={orgPreferencesGetResponse.current?.logoUrl}
            fieldName={FieldNames.LOGO}
            fieldValue={form.getFieldValue(FieldNames.LOGO)}
          />

          {canCustomizeLabels && (
            <>
              <h2 className="section-title">{t(`${TRANSLATION_ROOT}.auth-method-labels-title`)}</h2>
              <p>{t(`${TRANSLATION_ROOT}.auth-method-labels-description`)}</p>

              <Form.Item name={FieldNames.USERNAME_LABEL}>
                <InputBox
                  label={t(`${TRANSLATION_ROOT}.username-label`)}
                  placeholder={t(`${TRANSLATION_ROOT}.username-label-hint`)}
                  maxLength={AUTH_METHOD_LABEL_MAX_LENGTH}
                />
              </Form.Item>

              <Form.Item name={FieldNames.PASSWORD_LABEL}>
                <InputBox
                  label={t(`${TRANSLATION_ROOT}.password-label`)}
                  placeholder={t(`${TRANSLATION_ROOT}.password-label-hint`)}
                  maxLength={AUTH_METHOD_LABEL_MAX_LENGTH}
                />
              </Form.Item>

              <Form.Item name={FieldNames.PROX_CARD_LABEL}>
                <InputBox
                  label={t(`${TRANSLATION_ROOT}.prox-card-label`)}
                  placeholder={t(`${TRANSLATION_ROOT}.prox-card-label-hint`)}
                  maxLength={AUTH_METHOD_LABEL_MAX_LENGTH}
                />
              </Form.Item>
            </>
          )}
        </section>

        <div className="appearance-card btns-container">
          <Button
            type="text"
            className="text-button"
            onClick={fetchData}
            disabled={!isFormDirty}
            icon={<UndoOutlined className="undo-icon" />}
          >
            {t('common.undo')}
          </Button>
          <Button type="primary" htmlType="submit" disabled={isLoading || !isFormDirty}>
            {t('common.save')}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(Appearance);
